import { FC } from 'react';
import { CustomModal, SaveChangesModal, StatusBanner } from '@components';
import { Controller, Form, FormProvider } from 'react-hook-form';
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
} from '@mui/material';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { Select } from '@ui';
import { useEditReportFolderModal } from '@views/pages/Reporting/components/EditReportFolderModal/hooks/useEditReportFolderModal.ts';

interface AddReportFolderModalProps {
  isOpen: boolean;
  handleClose: () => void;
  report_folder_id: string;
}

export const EditReportFolderModal: FC<AddReportFolderModalProps> = ({
  isOpen,
  handleClose,
  report_folder_id,
}) => {
  const {
    isAddReportFolderPending,
    isValid,
    organizationOptions,
    onSubmit,
    formMethods,
    isDirty,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isEditSubmitting,
    handleCloseAction,
    containsAllowOthersToEdit,
  } = useEditReportFolderModal(handleClose, report_folder_id);

  const { control, watch } = formMethods;

  const showWarningBanner = watch('isPrivate');

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleCloseAction}
        title={t('reporting.editFolderModal.title')}
        isLoading={isAddReportFolderPending}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {showWarningBanner && containsAllowOthersToEdit && (
              <StatusBanner
                text={t('reporting.editFolderModal.bannerText')}
                variant={'warning'}
              />
            )}
            <Controller
              name='name'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('reporting.editFolderModal.name')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='organization_id'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  label={t('reporting.editFolderModal.organization')}
                  error={!isNil(error)}
                  required
                  options={organizationOptions}
                  helperText={error?.message}
                  disabled
                />
              )}
            />

            <Controller
              name='isPrivate'
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>,
                          checked,
                        ) => onChange(checked)}
                      />
                    }
                    label={t('reporting.editFolderModal.private')}
                  />
                </FormGroup>
              )}
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                left: 0,
                backgroundColor: 'white',
                pb: 2,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isAddReportFolderPending}
                onClick={handleCloseAction}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isAddReportFolderPending || !isValid || !isDirty}
              >
                {t('general.save')}
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </>
  );
};
